import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { signIn, updateProfile, updatePassword, register, getSMS } from '@/api/account';
import { removeAccount, loginCallBack } from '@/utils/user';
import { mergePermissionsByRoles, generatePermissionNameList } from '@/utils/permission';
import router, { resetRouter } from '@/router';
import { PermissionModule } from './permission';
import { asLogin } from '@/utils/asLoginRequest';
import { getActiveProjects } from '@/utils/project';
import { changeAccount } from '@/utils/request';

type IUserDetailInfo = IAuthenticate.IUserDetailInfo;
type ILogin = IAuthenticate.IRequest.ILogin;
type IUpdateProfile = IAuthenticate.IRequest.IUpdateProfile;
type IRegister = IAuthenticate.IRequest.IRegister;
type IPermissionInfo = IPermission.IResponse.IPermissionInfo;
type IProjectInfo = IProject.IResponse.IProjectInfo;

export interface IAccountState {
  name: string;
  token: string;
  userDetailInfo: IUserDetailInfo | null;
  permissions: IPermissionInfo[];
}

@Module({ dynamic: true, store, name: 'account' })
class Account extends VuexModule implements IAccountState {
  public name: string = '';
  public token: string = '';
  public userDetailInfo: IUserDetailInfo | null = null;
  public permissions: IPermissionInfo[] = [];
  public projects: IProjectInfo[] = [];
  @Action({rawError: true})
  public Login(params: ILogin): Promise<any> {
    return new Promise((reslove, reject) => {
      asLogin({
        username: params.mobile,
        password: params.password,
      }).then(() => {
        signIn(params).then((res) => {
          this.SET_NAME(res.user.name);
          this.SET_TOKEN(res.accessToken);
          this.SET_USER_DETAIL_INFO(res.user);
          this.UpdatePermissionsAndRoutes();
          this.initProjects();
          loginCallBack(params, res);
          changeAccount({
            token: res.accessToken,
          });
          reslove(res);
        }).catch((error) => {
          reject(error.message);
        });
      }).catch((error) => {
        reject(error.message);
      });
    });
  }
  @Action({rawError: true})
  public Register(params: IRegister): Promise<string> {
    return new Promise((reslove, reject) => {
      register(params).then((res) => {
        reslove(res.message);
      }).catch((error) => {
        reject(error.message);
      });
    });
  }
  @Action({rawError: true})
  public initProjects(): Promise<string> {
    return new Promise((reslove, reject) => {
      getActiveProjects({returnTeam: true}).then((projects) => {
        this.SET_PROJECTS(projects);
        reslove('');
      }).catch((err) => {
        reject(err);
      });
    });
  }
  @Action({rawError: true})
  public GetSMS(mobile: string): Promise<string> {
    return new Promise((reslove, reject) => {
      getSMS(mobile).then((res) => {
        reslove(res.message);
      }).catch((error) => {
        reject(error.message);
      });
    });
  }
  @Action({rawError: true})
  public UpdateProfile(params: IUpdateProfile): Promise<string> {
    return new Promise((reslove, reject) => {
      updateProfile(params).then((res) => {
        reslove(res.message);
        this.UPDATE_PROFILE(params);
      }).catch((error) => {
        reject(error.message);
      });
    });
  }
  @Action({rawError: true})
  public UpdatePassword(params: IAuthenticate.IRequest.IUpdatePassword): Promise<string> {
    return new Promise((reslove, reject) => {
      updatePassword(params).then((res) => {
        reslove(res.message);
      }).catch((error) => {
        reject(error.message);
      });
    });
  }
  @Action
  public ResetToken() {
    this.SET_TOKEN('');
  }
  @Action
  public Logout() {
    this.SET_TOKEN('');
    resetRouter();
    removeAccount();
  }
  @Action
  public UpdatePermissionsAndRoutes() {
    const permissions = mergePermissionsByRoles(this.userDetailInfo!.roles, this.userDetailInfo!.permissions);
    this.SET_PERMISSIONS(permissions);
    const permissionNames = generatePermissionNameList(permissions);
    // --- 根据权限更新 router
    resetRouter();
    PermissionModule.GenerateRoutes(permissionNames);
    router.addRoutes(PermissionModule.dynamicRoutes);
    // ---
  }
  @Mutation
  private SET_NAME(name: string) {
    this.name = name;
  }
  @Mutation
  private SET_TOKEN(token: string) {
    this.token = token;
  }
  @Mutation
  private SET_USER_DETAIL_INFO(userInfo: IUserDetailInfo | null) {
    this.userDetailInfo = userInfo;
  }
  @Mutation
  private SET_PERMISSIONS(permissions: IPermissionInfo[]) {
    this.permissions = permissions;
  }
  @Mutation
  private UPDATE_PROFILE(params: IUpdateProfile) {
    Object.assign(this.userDetailInfo, params);
  }
  @Mutation
  private SET_PROJECTS(projects: IProjectInfo[]) {
    let projectKeys: string[] = [];
    const teamKeys: string[] = this.userDetailInfo?.teams.map((team) => team.id) || [];
    // 找出 每个组 对应跟进的 projectId
    projects.filter((project) => {
      return project.teams.find((projectTeam) => teamKeys.includes(projectTeam.id));
    }).forEach((project) => {
      projectKeys.push(project.id);
    });
    projectKeys = [...new Set(projectKeys)]; // 去重
    this.projects = projects.filter((project) => projectKeys.includes(project.id)); // 根据projectId 筛选project
  }
}
export const AccountModule = getModule(Account);
export const getPermissionNames = () => AccountModule.permissions.map((permission) => permission.name);
